import { useEffect, useState } from "react";
import {
    AlertCircleIcon,
    DownloadIcon,
    FileQuestionIcon,
    FileTextIcon,
    Loader2Icon,
    XIcon,
} from "lucide-react";
import { PDFViewer } from "@component/pdf-viewer";
import { RUBRIC_LABELS } from "@type/training";
import {
    getKbDocumentPreviewUrl,
    type KbDocument,
    type KbExtractedFact,
    type KbScoringCategory,
} from "@service/knowledge-base";
import { cn } from "@util/tailwind";

/** Blue category chip used in the Related-categories column and this drawer. */
export function CategoryChip({ element }: { element: KbScoringCategory }) {
    return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
            {RUBRIC_LABELS[element]}
        </span>
    );
}

/**
 * Trainer-facing note per category. Deliberately phrased as "relates to", not
 * "used when scoring X": the categories are assigned by a display-side
 * extraction pass and the evaluator does NOT restrict a document to them — so
 * this is a browsing aid, never a scoring claim (Trainer KB Phase 0).
 */
const KB_SCORING_NOTE: Record<KbScoringCategory, string> = {
    product_knowledge:
        "Facts about products, services, pricing, and policies that relate to the Product Knowledge category.",
    problem_resolution:
        "Procedures and resolution steps that relate to the Problem Resolution category.",
    call_handling:
        "Call-flow and procedure references that relate to the Call Handling category.",
    empathy:
        "Guidance on acknowledging customers that relates to the Empathy category.",
    tone: "Guidance on delivery and style that relates to the Tone category.",
};

/** Group extracted facts by the scoring category they relate to, preserving order. */
function groupFactsByElement(
    facts: KbExtractedFact[],
): [KbScoringCategory, KbExtractedFact[]][] {
    const order: KbScoringCategory[] = [];
    const map = new Map<KbScoringCategory, KbExtractedFact[]>();
    for (const f of facts) {
        if (!map.has(f.element)) {
            map.set(f.element, []);
            order.push(f.element);
        }
        map.get(f.element)!.push(f);
    }
    return order.map((el) => [el, map.get(el)!]);
}

function formatFileSize(bytes: number): string {
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
    return `${Math.round(bytes / 1_000)} KB`;
}

function formatDate(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

/**
 * Whether a file URL can be embedded directly in an <iframe> / used as a
 * download href without an auth header — i.e. it's already a presigned S3 (or
 * GCS) URL, not our auth-protected `/documents/:id/file` API route. When the BE
 * returns a presigned URL we embed it as-is (cross-origin document loads don't
 * enforce CORS); otherwise we must fetch-with-auth and fall back.
 */
function isDirectlyEmbeddableUrl(url: string): boolean {
    return /amazonaws\.com|storage\.googleapis\.com|[?&](?:X-Amz-Signature|Signature|AWSAccessKeyId|X-Goog-Signature)=/i.test(
        url,
    );
}

/**
 * The Knowledge Base document drawer — Document Preview + Extracted
 * Information tabs. Extracted from the KB page so every surface that
 * references a document (KB table, "Knowledge used in this evaluation" card)
 * opens the SAME view instead of reimplementing it.
 */
export function KnowledgeDocDrawer({
    doc,
    onClose,
    initialTab = "preview",
}: {
    doc: KbDocument | null;
    onClose: () => void;
    initialTab?: "preview" | "extracted";
}) {
    const [drawerTab, setDrawerTab] = useState<"preview" | "extracted">(initialTab);
    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Reset the tab whenever a different document is opened.
    useEffect(() => {
        setDrawerTab(initialTab);
    }, [doc?.id, initialTab]);

    // Fetch the preview file (auth'd → blob URL) whenever a document is opened.
    // The BE `fileUrl` is an auth-protected route an <iframe> can't call
    // directly, so we fetch with the auth header and embed a blob URL instead.
    useEffect(() => {
        if (!doc) {
            setPreviewBlobUrl(null);
            setPreviewLoading(false);
            return;
        }
        const previewSource = doc.previewUrl ?? doc.fileUrl;
        if (!previewSource) {
            setPreviewBlobUrl(null);
            setPreviewLoading(false);
            return;
        }
        if (isDirectlyEmbeddableUrl(previewSource)) {
            setPreviewBlobUrl(previewSource);
            setPreviewLoading(false);
            return;
        }
        let objectUrl: string | null = null;
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewBlobUrl(null);
        getKbDocumentPreviewUrl(previewSource, doc.fileType)
            .then((url) => {
                if (cancelled) {
                    if (url) URL.revokeObjectURL(url);
                    return;
                }
                objectUrl = url;
                setPreviewBlobUrl(url);
            })
            .finally(() => {
                if (!cancelled) setPreviewLoading(false);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [doc]);

    if (!doc) return null;

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            onClick={onClose}
        >
            <div
                className="bg-white w-full sm:w-[600px] md:w-[700px] lg:w-1/2 h-full flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Document Preview
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Document meta */}
                <div className="px-6 pt-5 pb-4 flex-shrink-0">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <FileTextIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                                {doc.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {doc.fileType} · {formatFileSize(doc.sizeBytes)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Uploaded by{" "}
                                <span className="font-medium text-gray-700">
                                    {doc.uploadedBy.name}
                                </span>{" "}
                                · {formatDate(doc.uploadedAt)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 flex-shrink-0">
                    <div className="flex px-6">
                        {(
                            [
                                { k: "preview", label: "Document Preview" },
                                { k: "extracted", label: "Extracted Information" },
                            ] as const
                        ).map((t) => {
                            const active = drawerTab === t.k;
                            return (
                                <button
                                    key={t.k}
                                    onClick={() => setDrawerTab(t.k)}
                                    className={cn(
                                        "py-2.5 px-4 text-sm font-medium relative whitespace-nowrap",
                                        active
                                            ? "text-blue-600"
                                            : "text-gray-500 hover:text-gray-700",
                                    )}
                                >
                                    {t.label}
                                    {active && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Document Preview tab */}
                {drawerTab === "preview" && (
                    <div className="flex-1 overflow-auto bg-gray-100 p-6">
                        {previewLoading ? (
                            <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-center rounded-lg border border-gray-200 bg-white">
                                <Loader2Icon className="w-8 h-8 text-gray-300 animate-spin" />
                                <p className="text-sm text-gray-500 mt-3">Loading preview…</p>
                            </div>
                        ) : doc.fileType === "PDF" && previewBlobUrl ? (
                            <PDFViewer
                                url={previewBlobUrl}
                                className="min-h-[480px] rounded-lg border border-gray-200 overflow-hidden bg-white"
                            />
                        ) : (
                            <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-center rounded-lg border border-dashed border-gray-300 bg-white px-6">
                                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <FileQuestionIcon className="w-7 h-7 text-gray-400" />
                                </div>
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                    {doc.fileType === "DOCX"
                                        ? "Preview not available for DOCX"
                                        : (doc.previewUrl ?? doc.fileUrl)
                                          ? "Couldn't load preview"
                                          : "Preview not available yet"}
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    {doc.fileType === "DOCX"
                                        ? "Word documents can't be shown inline. Download the original file to view it."
                                        : (doc.previewUrl ?? doc.fileUrl)
                                          ? "Something went wrong loading this document. You can download the original file instead."
                                          : "This document doesn't have a file to preview yet."}
                                </p>
                                {previewBlobUrl && (
                                    <a
                                        href={previewBlobUrl}
                                        download={doc.name}
                                        className="inline-flex items-center gap-2 mt-5 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                    >
                                        <DownloadIcon className="w-4 h-4" />
                                        Download original
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Extracted Information tab */}
                {drawerTab === "extracted" && (
                    <div className="flex-1 overflow-auto p-6">
                        {doc.facts && doc.facts.length > 0 ? (
                            <>
                                {doc.summary && (
                                    <div className="mb-5 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
                                            Summary
                                        </p>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            {doc.summary}
                                        </p>
                                    </div>
                                )}
                                <h3 className="text-base font-semibold text-gray-900">
                                    Extracted Information
                                </h3>
                                <p className="text-xs text-gray-500 leading-relaxed mt-1 mb-5">
                                    Key facts the AI extracted from this document, grouped
                                    by the scoring category they relate to.
                                </p>
                                <div className="space-y-4">
                                    {groupFactsByElement(doc.facts).map(
                                        ([element, facts]) => (
                                            <div
                                                key={element}
                                                className="rounded-lg border border-gray-200 p-4"
                                            >
                                                <h4 className="text-sm font-semibold text-gray-900">
                                                    {RUBRIC_LABELS[element]}
                                                </h4>
                                                <p className="text-xs text-gray-500 leading-relaxed mt-1">
                                                    {KB_SCORING_NOTE[element]}
                                                </p>
                                                <ul className="space-y-2 mt-3 pt-3 border-t border-gray-100">
                                                    {facts.map((f, i) => (
                                                        <li
                                                            key={i}
                                                            className="flex items-start gap-2 text-sm text-gray-700"
                                                        >
                                                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                            <span className="leading-relaxed">
                                                                {f.fact}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ),
                                    )}
                                </div>
                            </>
                        ) : doc.extractionStatus === "pending" ||
                          doc.extractionStatus === "processing" ? (
                            <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-center">
                                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                                    <Loader2Icon className="w-7 h-7 text-blue-500 animate-spin" />
                                </div>
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                    Extracting information…
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    We're analysing this document. Its key facts and the
                                    scoring categories they relate to will appear here once
                                    extraction finishes.
                                </p>
                            </div>
                        ) : doc.extractionStatus === "failed" ? (
                            <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-center">
                                <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mb-4">
                                    <AlertCircleIcon className="w-7 h-7 text-rose-500" />
                                </div>
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                    Couldn't extract information
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    Something went wrong analysing this document. Try
                                    re-uploading it.
                                </p>
                            </div>
                        ) : (
                            <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-center">
                                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <FileQuestionIcon className="w-7 h-7 text-gray-400" />
                                </div>
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                    No extracted information yet
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    No key facts were extracted from this document.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
